import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getInstellingen, getWatAls, putInstellingen } from '../work-quality.api';
import type { InstellingenAntwoord, InstellingenWijziging, WatAlsAntwoord, WatAlsDoelAntwoord } from '../work-quality.api';

export interface Lezing<T> {
  data?: T;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * De drempel die nu geldt (story 4.5, AD-13).
 *
 * Geen verversing bij vensterfocus, net als elke andere lezing van deze module: een waarde die
 * onder de handen van een beheerder verspringt terwijl zij aan het typen is, is erger dan een
 * waarde die pas na het opslaan bijtrekt.
 */
export function useInstellingen(): Lezing<InstellingenAntwoord> {
  const query = useQuery({
    queryKey: ['workQuality', 'settings'],
    queryFn: getInstellingen,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Wat een drempelwaarde met deze week zou doen — de wat-als van AD-13.
 *
 * `drempel` is `null` zodra er in het veld geen drempel staat, en dán wordt er niets opgehaald: een
 * halve of ongeldige waarde hoort niet als vraag de deur uit te gaan, en de dienst zou er terecht
 * een 400 op geven die op het scherm als storing zou lezen.
 *
 * Elke waarde krijgt haar eigen sleutel, zodat het antwoord van de vorige waarde nooit naast de
 * nieuwe drempel op het scherm kan blijven staan: react-query begint dan aan een nieuwe lezing en
 * `data` is ondertussen leeg. Dat is precies wat het criterium vraagt — het gevolg hoort bij de
 * waarde die er staat, of er staat nog geen gevolg.
 */
export function useWatAls(week: string, drempel: number | null): Lezing<WatAlsAntwoord> {
  const query = useQuery({
    queryKey: ['workQuality', 'what-if', week, drempel],
    queryFn: () => getWatAls(week, { drempel: drempel! }),
    enabled: week !== '' && drempel !== null,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Wat een doel met deze week zou doen: wie er in groep Aandacht valt (story 8, contract §4).
 *
 * Dezelfde regels als {@link useWatAls}, onder een eigen sleutel: een antwoord over een drempel en een
 * antwoord over een doel hebben een andere vorm, en mogen nooit voor elkaar in de cache staan.
 */
export function useWatAlsDoel(week: string, doel: number | null): Lezing<WatAlsDoelAntwoord> {
  const query = useQuery({
    queryKey: ['workQuality', 'what-if-doel', week, doel],
    queryFn: () => getWatAls(week, { doel: doel! }),
    enabled: week !== '' && doel !== null,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Waarom het opslaan niet lukte, in twee gevallen en geen twintig.
 *
 * `geweigerd` is de waarde zelf: de tweede grens vond er geen drempel in. Al het andere is
 * `mislukt` — een storing, een verlopen sessie, een netwerk dat wegviel. Twee gevallen omdat het
 * scherm er twee verschillende dingen over te zeggen heeft: bij het eerste ligt de volgende stap
 * bij de beheerder, bij het tweede niet. De foutmelding zelf komt nooit op het scherm; wat een
 * lezer aan `403` of `Request failed with status code 400` heeft is niets.
 */
export type OpslagFout = 'geweigerd' | 'mislukt';

export interface DrempelOpslag {
  opslaan: (drempel: number) => void;
  isBezig: boolean;
  isGelukt: boolean;
  fout: OpslagFout | null;
  /** De waarde die werkelijk is weggeschreven, zoals de dienst haar terugmeldde. */
  opgeslagenDrempel: number | null;
  /**
   * De versie waarin die waarde is vastgelegd — het wijzigingsmoment, en dus altijd hoger dan de
   * vorige.
   *
   * Het scherm toont dit getal nergens; het gebruikt het als sleutel onder de bevestiging. Twee
   * opslagacties met dezelfde uitkomstzin leveren anders identieke tekst in hetzelfde levende
   * gebied op, en een schermlezer kondigt de tweede dan niet aan.
   */
  opgeslagenVersie: number | null;
}

function isAfwijzing(fout: unknown): boolean {
  const status = (fout as { response?: { status?: unknown } } | null)?.response?.status;
  return status === 400;
}

/**
 * De drempel verzetten, en daarna opnieuw lezen wat er staat.
 *
 * De bevestiging leunt op `opgeslagenDrempel` — de waarde uit het antwoord van de dienst en niet de
 * waarde die in het veld stond. Dat is het verschil tussen "wij hebben iets verstuurd" en "dit is
 * opgeslagen", en het is precies de bewering die de bevestiging doet.
 *
 * Na een geslaagde opslag wordt alles onder `['workQuality']` ongeldig gemaakt (story 8): drempel
 * en doel bepalen wat Team en Mijn feedback tonen, en die moeten de nieuwe waarde laten zien zonder
 * de `staleTime` van vijf minuten af te wachten.
 */
function useInstellingOpslaan<T>(wijziging: (waarde: T) => InstellingenWijziging) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (waarde: T) => putInstellingen(wijziging(waarde)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workQuality'] });
    },
  });
}

export function useDrempelOpslaan(): DrempelOpslag {
  const mutation = useInstellingOpslaan((drempel: number) => ({ drempelProductenPerPersoon: drempel }));

  return {
    opslaan: (drempel: number) => mutation.mutate(drempel),
    isBezig: mutation.isPending,
    isGelukt: mutation.isSuccess,
    fout: mutation.error === null ? null : isAfwijzing(mutation.error) ? 'geweigerd' : 'mislukt',
    opgeslagenDrempel: mutation.data?.data.drempelProductenPerPersoon ?? null,
    opgeslagenVersie: mutation.data?.data.instellingVersie ?? null,
  };
}

export interface DoelOpslag {
  opslaan: (doel: number) => void;
  isBezig: boolean;
  isGelukt: boolean;
  fout: OpslagFout | null;
  /** Het doel zoals de dienst het terugmeldde — niet wat er in het veld stond. */
  opgeslagenDoel: number | null;
  opgeslagenVersie: number | null;
}

/** Het doel verzetten — dezelfde regels als {@link useDrempelOpslaan}, voor het andere veld. */
export function useDoelOpslaan(): DoelOpslag {
  const mutation = useInstellingOpslaan((doel: number) => ({ doelInEenKeerGoed: doel }));

  return {
    opslaan: (doel: number) => mutation.mutate(doel),
    isBezig: mutation.isPending,
    isGelukt: mutation.isSuccess,
    fout: mutation.error === null ? null : isAfwijzing(mutation.error) ? 'geweigerd' : 'mislukt',
    opgeslagenDoel: mutation.data?.data.doelInEenKeerGoed ?? null,
    opgeslagenVersie: mutation.data?.data.instellingVersie ?? null,
  };
}
